import { useQuery } from '@tanstack/react-query';
import type {
  ClaimReason,
  ClaimSort,
  ClaimStatus,
  ClaimSummary,
  ClaimType,
} from '@/lib/api-admin/types';
import type { ClaimSearchType } from '@/stores/useClaimFiltersStore';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

export interface ClaimsQueryParams {
  page?: number;
  size?: number;
  sort?: ClaimSort;
  q?: string;
  searchType?: ClaimSearchType | null;
  claimStatus?: ClaimStatus | null;
  claimType?: ClaimType | null;
  reasonCode?: ClaimReason | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  orderDateFrom?: string | null;
  orderDateTo?: string | null;
}

export interface ClaimsPage {
  items: ReadonlyArray<ClaimSummary>;
  meta: ApiMeta;
}

interface AdminClaimListQuery {
  page?: number;
  size?: number;
  sort?: ClaimSort;
  q?: string;
  claimStatus?: ClaimStatus;
  claimType?: ClaimType;
  reasonCode?: ClaimReason;
  dateFrom?: string;
  dateTo?: string;
  searchType?: Exclude<ClaimSearchType, 'ALL'>;
  orderDateFrom?: string;
  orderDateTo?: string;
}

export const cleanQuery = (params: ClaimsQueryParams): AdminClaimListQuery => {
  const out: AdminClaimListQuery = {};
  if (params.page) out.page = Math.max(0, params.page - 1);
  if (params.size) out.size = params.size;
  // QA-327: 정렬을 BE 에 보낸다. 종전엔 F-08(정렬 컬럼명 불일치) 때 꺼 둔 채라 BE 기본값(@PageableDefault
  //   sort=requestedAt, 방향 기본 = 오름차순)만 적용됐다 — 화면은 내림차순(↓)을 표시하는데 실제는 오름차순이고,
  //   화살표를 눌러도 바뀌지 않았다. 지금 BE(OrderClaim)에 requestedAt·completedAt·claimStatus 필드가 모두 있다.
  if (params.sort) out.sort = params.sort;
  if (params.q) out.q = params.q;
  // QA-626 — 검색어 유형·주문일(BE 요청 중). 유형은 q 와 함께 보내 BE 반영 전엔 통합검색으로 찾힌다.
  if (params.q && params.searchType && params.searchType !== 'ALL') out.searchType = params.searchType;
  if (params.orderDateFrom) out.orderDateFrom = params.orderDateFrom;
  if (params.orderDateTo) out.orderDateTo = params.orderDateTo;
  if (params.claimStatus) out.claimStatus = params.claimStatus;
  if (params.claimType) out.claimType = params.claimType;
  if (params.reasonCode) out.reasonCode = params.reasonCode;
  if (params.dateFrom) out.dateFrom = params.dateFrom;
  if (params.dateTo) out.dateTo = params.dateTo;
  return out;
};

export const claimsQueryKey = (params: ClaimsQueryParams) =>
  ['admin', 'claims', params] as const;

export const useClaimsQuery = (params: ClaimsQueryParams) =>
  useQuery<ClaimsPage>({
    queryKey: claimsQueryKey(params),
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/api/admin/v1/claims', {
        params: { query: cleanQuery(params) },
      });
      const payload = data as { items: ClaimSummary[] };
      return { items: payload.items, meta };
    },
  });
